import 'swiper/css';
import 'swiper/css/a11y';
import styles from './Carousel.module.scss';

import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import React, { AllHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import { CarouselAssembly, CarouselType, Topic } from 'api/contentful/contentTypes.cloverdotcom';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { ArrowButton, ArrowType } from 'components/ArrowButton/ArrowButton';
import { Heading } from 'components/CarouselModule/CarouselHeading/CarouselHeading';
import {
  CAROUSEL_EVENT_DELAY,
  CAROUSEL_MODULE_TEST_ID,
} from 'components/CarouselModule/CarouselModule.const';
import { computeSliderProps } from 'components/CarouselModule/SliderSettingFunctions';
import { CircleArrowButton } from 'components/CircleArrowButton/CircleArrowButton';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { FlatEntry, flattenContentfulData } from 'services/cms/flattenContentful';
import { componentKey, getField } from 'utils/EntryUtil';
import { setAriaLive } from 'utils/setAriaLive';
import { AccordionCarousel } from './AccordionCarousel/AccordionCarousel';
import { CarouselItem } from './CarouselItem';
import { DeviceItemContent } from './DeviceItemContent/DeviceItemContent';
import { DeviceNavigation } from './DeviceNavigation/DeviceNavigation';
import { areAllSlidesNotVisible } from './utils';

interface ICarouselModule extends AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<CarouselAssembly>;
}

interface IArrowSlide {
  allowSlidePrev?: boolean;
  allowSlideNext?: boolean;
  isBeginning?: boolean;
  isEnd?: boolean;
}

interface ISwiperParams {
  slidesOffsetBefore: number;
  slidesOffsetAfter: number;
}

export interface ISwiperClass {
  slidePrev: () => void;
  slideNext: () => void;
  slideTo: (index: number) => void;
  update: () => void;
  slidesEl?: HTMLElement;
}

export const CarouselModule: React.FC<ICarouselModule> = ({ entry, ...props }) => {
  const { t } = useTranslation();
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isDesktop = browser.is.desktop;
  const [{ allowSlidePrev, allowSlideNext, isBeginning, isEnd }, setEdge] = useState<IArrowSlide>({});

  const apiRef = useRef<{
    swiper: IArrowSlide &
      ISwiperClass & {
        params?: ISwiperParams;
        hostEl?: HTMLElement;
        wrapperEl?: HTMLElement;
        slides?: HTMLElement[];
      };
  } | null>(null);
  const ariaLiveRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const shouldDisplayNavigation = areAllSlidesNotVisible(
    apiRef.current?.swiper?.slidesEl?.children,
    headingRef.current,
  );

  const { autoPlay, carouselType, items, style, swiperProps } = useMemo(() => {
    const carouselType = getField(entry, 'carouselType');
    const autoPlay = getField(entry, 'autoPlay');
    const style = { backgroundColor: getField(entry, 'backgroundColor') };
    const items = getField(entry, 'items', []).filter(Boolean);
    if (!carouselType || !items.length) return {};
    const props = computeSliderProps(carouselType);
    const loop: boolean = items.length > 1 ? !!props.loop : false;
    return {
      autoPlay,
      carouselType,
      items: loop ? [...items, ...items] : items, // add additional items to make sure the carousel loops naturally
      style,
      swiperProps: {
        ...props,
        loop,
      } as SwiperProps,
    };
  }, [entry]);

  const isTypeProduct = carouselType === CarouselType.PRODUCT;
  const isTypeFeature = carouselType === CarouselType.FEATURE;
  const isTypeSquare = carouselType === CarouselType.SQUARE;
  const isTypeHero = carouselType === CarouselType.HERO;
  const isTypeAccordion = carouselType === CarouselType.ACCORDION;
  const isTypeAccordionCaseStudy = carouselType === CarouselType.ACCORDION_CASE_STUDY;
  const isTypeDevice = carouselType === CarouselType.DEVICE;

  useEffect(() => {
    if (!isTypeSquare) return;
    // Change slidesOffsetBefore and slidesOffsetAfter dynamically
    // https://muut.com/i/swiper/general:change-slidesoffsetbefore-a
    let prevOffset = 0;
    function update() {
      // Set up offset
      const offset = headingRef.current?.getBoundingClientRect().x;
      if (!offset || !apiRef.current?.swiper?.params || prevOffset === offset) return;
      prevOffset = offset;
      apiRef.current.swiper.params.slidesOffsetBefore = offset;
      apiRef.current.swiper.params.slidesOffsetAfter = offset;
      // Setup max height
      if (
        apiRef.current.swiper.wrapperEl &&
        apiRef.current.swiper.slides &&
        apiRef.current.swiper.slides.length > 0
      ) {
        const cardHeights = apiRef.current.swiper.slides.map(
          slide => slide.querySelector('.square-card')?.getBoundingClientRect().height || 0,
        );
        const maxHeight = Math.max(...cardHeights);
        if (maxHeight > 0) {
          apiRef.current.swiper.wrapperEl.style.height = `${maxHeight}px`;
        }
      }
      // Then update swiper
      apiRef.current.swiper.update();
    }

    window.addEventListener('resize', update);
    update();

    return () => {
      window.removeEventListener('resize', update);
    };
  }, [isTypeSquare]);

  const updateEdge = useCallback(
    swiper => {
      const { allowSlidePrev, allowSlideNext, isBeginning, isEnd, slidesEl } = apiRef.current?.swiper || {};
      let isLastSlideInViewport = isEnd;

      if ((isTypeSquare || isTypeAccordionCaseStudy) && slidesEl) {
        const slidesArray = Array.from(slidesEl.children);
        const lastSlideElement = slidesArray[slidesArray.length - 1];
        isLastSlideInViewport = lastSlideElement?.getBoundingClientRect()?.right < window.innerWidth;
      }

      setEdge({ allowSlidePrev, allowSlideNext, isBeginning, isEnd: isLastSlideInViewport });
      setActiveIndex(swiper.activeIndex);
    },
    [isTypeSquare, isTypeAccordionCaseStudy],
  );

  if (!items || !carouselType) return null;

  const renderHeadingAboveSlider =
    isTypeSquare || isTypeProduct || isTypeAccordion || (isTypeFeature && isMobile);
  const renderHeadingInsideSlider = isTypeFeature && !isMobile;

  const onClickPrev = () => {
    if (!allowSlidePrev || isBeginning) return;
    apiRef.current?.swiper.slidePrev();
    setAriaLive(ariaLiveRef.current, t('carousel.show-previous-items'));
  };

  const onClickNext = () => {
    if (!allowSlideNext || isEnd) return;
    apiRef.current?.swiper.slideNext();
    setAriaLive(ariaLiveRef.current, t('carousel.show-more-items'));
  };

  const slideTo = (index: number) => {
    apiRef.current?.swiper.slideTo(index);
  };

  const circleArrowButtons = (
    <div className={cx(styles.carouselButtons, isTypeDevice && styles.carouselButtonsDevice)}>
      <CircleArrowButton
        aria-disabled={isBeginning ? 'true' : undefined}
        aria-label={t('carousel.previous-item')}
        onClick={onClickPrev}
        variant="left"
      />
      <CircleArrowButton
        aria-disabled={isEnd ? 'true' : undefined}
        aria-label={t('carousel.next-item')}
        onClick={onClickNext}
        variant="right"
      />
    </div>
  );

  if (isDesktop && (isTypeAccordion || isTypeAccordionCaseStudy)) {
    return <AccordionCarousel entry={flattenContentfulData(entry)} {...props} />;
  }

  const noImage = !items.some(item => item.fields?.image?.fields?.file?.url);
  const showArrowButtons = !isTypeAccordion && !isTypeDevice && !isTypeSquare && !isTypeAccordionCaseStudy;
  const showCircleArrowButtons =
    isTypeAccordion || (isTypeSquare && shouldDisplayNavigation) || isTypeAccordionCaseStudy;

  return (
    <section
      data-testid={isTypeFeature ? CAROUSEL_MODULE_TEST_ID.pageCarousel : null}
      className={cx(
        styles.carouselWrapper,
        isTypeSquare && styles.square,
        isTypeDevice && styles.carouselWrapperDevice,
        isTypeAccordionCaseStudy && styles.isCaseStudy,
      )}
      {...props}
    >
      <FadeToastUp>
        <div className={cx(styles.carousel, styles.heading)}>
          {renderHeadingAboveSlider && (
            <Heading entry={entry} carouselType={carouselType} headingRef={headingRef} />
          )}
          {isTypeDevice && (
            <DeviceNavigation
              items={items.map(i => flattenContentfulData(i)) as FlatEntry<Topic>[]}
              activeIndex={activeIndex}
              onButtonClick={slideTo}
              onPanPrev={onClickPrev}
              onPanNext={onClickNext}
            />
          )}
        </div>
      </FadeToastUp>
      <FadeToastUp>
        <div className={cx(styles.carousel, styles.content, isTypeDevice && styles.carouselDevice)}>
          <Swiper
            {...swiperProps}
            className={cx(styles.carouselSlider, {
              [styles.productCarouselSlider]: carouselType === CarouselType.PRODUCT,
              [styles.carouselSliderDevice]: carouselType === CarouselType.DEVICE,
            })}
            resizeObserver
            ref={apiRef}
            onInit={debounce(updateEdge, CAROUSEL_EVENT_DELAY)}
            onSlideChange={debounce(updateEdge, CAROUSEL_EVENT_DELAY)}
            onReachEnd={updateEdge}
            onTransitionEnd={updateEdge}
            onReachBeginning={updateEdge}
            onResize={updateEdge}
          >
            {renderHeadingInsideSlider && (
              <SwiperSlide className={styles.swiperSlide}>
                <Heading entry={entry} carouselType={carouselType} className={styles.headingInsideSlider} />
              </SwiperSlide>
            )}
            {items.map((entry, index) => (
              <SwiperSlide key={componentKey(entry, index)} className={styles.swiperSlide}>
                {(slideProps: unknown) => (
                  <CarouselItem
                    {...{
                      slideProps,
                      entry,
                      carouselType,
                      hostEl: apiRef.current?.swiper?.hostEl,
                      style,
                      autoPlay: isTypeHero ? autoPlay : undefined,
                      noImage,
                    }}
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          {isTypeDevice && items[activeIndex] && (
            <DeviceItemContent
              entry={flattenContentfulData(items[activeIndex])}
              onPanPrev={onClickPrev}
              onPanNext={onClickNext}
            >
              {circleArrowButtons}
            </DeviceItemContent>
          )}
          {showCircleArrowButtons && circleArrowButtons}
          {showArrowButtons && (
            <>
              {allowSlidePrev && (
                <ArrowButton
                  aria-disabled={isBeginning ? 'true' : undefined}
                  arrowDirection={ArrowType.PREV}
                  carouselType={carouselType}
                  className={styles.prevButton}
                  onClick={onClickPrev}
                />
              )}
              {allowSlideNext && (
                <ArrowButton
                  aria-disabled={isEnd ? 'true' : undefined}
                  arrowDirection={ArrowType.NEXT}
                  carouselType={carouselType}
                  className={styles.nextButton}
                  infinite={swiperProps?.loop}
                  onClick={onClickNext}
                />
              )}
            </>
          )}
          {(allowSlidePrev || allowSlideNext) && <div data-testid="aria-live" ref={ariaLiveRef} />}
          {isTypeHero && <Heading entry={entry} carouselType={carouselType} />}
        </div>
      </FadeToastUp>
    </section>
  );
};

export default CarouselModule;
